<template>
  <!--
    PoolaShareAction — the thin wrapper around the UNCHANGED PlatformShare
    (home-feed parity). Mounted by PoolaOfferCard only for non-standalone offers
    in the scheduled/active phases (share-flow plan S4). A CONTENT FRAGMENT (no
    <f7-page>); renders inline so PlatformShare's own f7-popup is never nested
    inside another sheet.

    DEFENSIVE CONSUME-TIME GUARD (why this isn't a one-liner):
    usePoolaOffers threads each offer's auto-included post+media into the SHARED
    Posts pinia-orm repo on fetch — but that repo is shared with the home feed,
    and usePostsFeed's repo.flush() on a home-tab refresh can evict the post out
    from under us. When that happens the offer still reports hasShareablePost
    (postId stays set), yet PlatformShare's useRepo(Post).with('media').find()
    silently resolves null and the share button does nothing. So before mounting
    PlatformShare we verify the post is actually in the repo; if it was evicted
    we lazy-refetch the single offer (?include=post,post.media is allow-listed
    per the FE contract Section 3.1) and re-save it before enabling share.
  -->
  <div v-if="hasPost" class="poola-share-action">
    <!-- Post present in the repo → reuse PlatformShare exactly as the home feed. -->
    <platform-share
      v-if="isPostReady"
      :post-id="offer.postId"
      @share-success="emit('share-success', $event)"
    ></platform-share>

    <!-- Post evicted from the shared repo → re-hydrating the single offer. -->
    <div v-else-if="isHydrating" class="poola-share-action__pending">
      <f7-preloader size="22"></f7-preloader>
      <span class="poola-share-action__pending-text">{{ t('poola.share.cta') }}</span>
    </div>

    <!-- Hydration failed (offer dropped its post, or the refetch errored). -->
    <f7-button v-else-if="hydrationFailed" class="poola-share-action__retry" @click="hydratePost">{{
      t('poola.buy.claim.refreshOffers')
    }}</f7-button>
  </div>
</template>

<script setup>
import { useRepo } from 'pinia-orm';
import { computed, onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import PlatformShare from '@/components/posts/PlatformShare.vue';
import { jsonApiClient } from '@/httpConfig/config/axios.js';
import Post from '@/models/posts/Post.js';

const props = defineProps({
  // The PoolaOffer (model instance or plain row). Must carry `id` (the single-
  // offer show path param) and `postId` (the linked Posts-repo id; null ⇒
  // standalone, no share affordance).
  offer: {
    type: Object,
    required: true,
  },
});

const emit = defineEmits(['share-success']);

const { t } = useI18n({ useScope: 'global' });

const postRepo = useRepo(Post);

const isHydrating = ref(false);
const hydrationFailed = ref(false);

// Standalone offers (postId == null) carry no shareable post — render nothing.
const hasPost = computed(() => props.offer?.postId != null);

// Reactive over the pinia-orm store: flips the moment the post lands in (or is
// evicted from) the shared Posts repo. Same query shape PlatformShare gates on.
const isPostReady = computed(() => hasPost.value && postRepo.find(props.offer.postId) != null);

/**
 * Ensure the linked post is present in the shared Posts repo before PlatformShare
 * mounts. No-op when the post is already there (the common path — usePoolaOffers
 * persisted it on fetch). Only fires the single-offer refetch when the post was
 * evicted (e.g. by usePostsFeed's repo.flush() on a home-tab refresh).
 */
async function hydratePost() {
  if (!hasPost.value || isPostReady.value || isHydrating.value) return;

  isHydrating.value = true;
  hydrationFailed.value = false;
  try {
    // Single-offer show: ?include=post,post.media is allow-listed (FE contract
    // Section 3.1). jsonApiClient deserializes (jsona) → res.data.data is the offer
    // with `post` (and post.media) nested. Re-save the post exactly as
    // usePoolaOffers does so morph media persists and PlatformShare resolves it.
    const res = await jsonApiClient.get(`/poola-offers/${props.offer.id}`, {
      params: { include: 'post,post.media' },
    });
    const post = res?.data?.data?.post ?? null;
    if (post) {
      postRepo.save(post);
    } else {
      // The offer no longer has a linked post — don't loop refetching.
      hydrationFailed.value = true;
    }
  } catch (err) {
    hydrationFailed.value = true;
    // eslint-disable-next-line no-console
    console.error('[PoolaShareAction] post re-hydration failed:', err);
  } finally {
    isHydrating.value = false;
  }
}

onMounted(hydratePost);
// A later eviction (post leaves the repo while mounted) or a new offer id
// re-checks. Acting only when the post is genuinely absent avoids the
// save→ready self-trigger looping.
watch(
  () => [props.offer?.id, isPostReady.value],
  () => {
    if (hasPost.value && !isPostReady.value && !isHydrating.value) hydratePost();
  },
);
</script>

<style scoped>
.poola-share-action {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.poola-share-action__pending {
  display: flex;
  align-items: center;
  gap: 10px;
  color: var(--poolit-text-muted, #9ca3af);
  font-size: 14px;
}

.poola-share-action__retry {
  --f7-button-text-color: var(--poolit-primary-600);
  align-self: flex-start;
  font-weight: 600;
}
</style>
